<template>
    <select
        v-if="native"
        ref="native-dropdown"
        class="dropdown dropdown--native"
        :required="required"
        @input="onOptionSelectNative"
        @click.stop="onOptionsOpen"
    >
        <option
            v-for="(option, index) in options"
            :key="index"
            :value="option.value"
        >
            {{ option.text }}
        </option>
    </select>

    <div
        v-else
        class="dropdown"
        :class="themeClass"
        @click.stop="onOptionsOpen"
    >
        <div class="dropdown__text">
            <span>{{ selectedText }}</span>

            <i class="dropdown__arrow">
                <font-awesome-icon :icon="['fal', 'chevron-down']" />
            </i>
        </div>

        <div
            ref="list"
            v-on-clickaway="onOptionsClose"
            class="dropdown__list"
            :class="listClasses"
            :style="listStyles"
        >
            <div
                v-for="(option, index) in options"
                :key="index"
                class="dropdown__item"
                :class="{ [STATE_CLASSES.DISABLED]: option.disabled }"
                :title="option.title"
                @click.stop="onOptionSelect(option)"
                @mouseenter.stop="onOptionHover(option)"
            >
                {{ option.text }}
            </div>

            <div
                v-if="specialOption"
                key="special-option"
                class="dropdown__item-special"
                @click.stop="onOptionSelect(specialOption)"
                @mouseenter.stop="onOptionHover(specialOption)"
            >
                {{ specialOption.text }}
            </div>
        </div>
    </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

import listenOnRootMixin from '@/mixins/listen-on-root';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    mixins: [clickaway, listenOnRootMixin],

    props: {
        options: {
            type: Array,
            default: () => [
                { text: 'option 1', value: 1 },
                { text: 'option 2', value: 2 },
                { text: 'option 3', value: 3 },
            ],
        },

        specialOption: {
            type: Object,
            default: null,
        },

        placeholder: {
            type: String,
            default: 'Select Option',
        },

        theme: {
            type: String,
            default: '', // themes = "minimalistic" and "underline"
        },

        value: {
            type: null,
            default: '',
        },

        native: {
            type: Boolean,
            default: false,
        },

        // max dropdown list height (in pixels)
        // no max height is applied if set to zero
        maxListHeight: {
            type: Number,
            default: 0,
        },

        required: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            showDirection: 'down',
            showOptions: false,
            selectedText: this.placeholder,
            STATE_CLASSES,
        };
    },

    computed: {
        allOptions() {
            return this.specialOption
                ? [...this.options, this.specialOption]
                : this.options;
        },

        listStyles() {
            if (this.maxListHeight <= 0) return null;

            return {
                maxHeight: `${ this.maxListHeight }px`,
            };
        },

        listClasses() {
            const listClasses = [];

            listClasses.push(this.showOptions ? STATE_CLASSES.VISIBLE : STATE_CLASSES.HIDDEN);
            listClasses.push(`dropdown__list--${ this.showDirection }`);

            return listClasses;
        },

        themeClass() {
            return {
                [`dropdown--${ this.theme }`]: !!this.theme,
            };
        },
    },

    watch: {
        value: {
            handler() {
                this.onOptionsUpdate();
            },
            immediate: true,
        },

        options: {
            handler() {
                this.onOptionsUpdate();
            },
            immediate: true,
        },
    },

    created() {
        // needed as v-clickaway doesn't trigger in certain situations
        this.listenOnRoot('flex::dropdown::close', this.onOptionsClose);
    },

    methods: {
        onOptionsOpen() {
            this.$root.$emit('flex::dropdown::close');

            if (!this.native) {
                const selectElem = this.$el;
                const selectElemY = selectElem.getBoundingClientRect().y;
                const listElem = this.$refs.list;

                const spaceAbove = selectElemY;
                const spaceBelow = window.innerHeight - (selectElemY + selectElem.offsetHeight);

                switch (true) {
                    case spaceBelow >= (listElem.offsetHeight + selectElem.offsetHeight):
                        this.showDirection = 'up';
                        break;

                    case spaceAbove >= listElem.offsetHeight:
                        this.showDirection = 'down';
                        break;

                    default:
                        this.showDirection = 'up';
                }
            }

            this.showOptions = true;
        },

        onOptionSelect(val) {
            if (val.disabled) return;

            this.selectedText = val.text;
            this.$emit('input', val.value);

            // this prevents the click event from also firing on any elements directly behind the dropdown list
            window.setTimeout(() => this.onOptionsClose(), 0);
        },

        onOptionSelectNative(e) {
            this.onOptionsClose();
            this.$emit('input', e.target.value);
        },

        onOptionHover(val) {
            this.$emit('mouseover', val.value);
        },

        async onOptionsUpdate() {
            await this.$nextTick();

            const nativeDropdown = this.$refs['native-dropdown'];

            if (this.native && nativeDropdown) {
                nativeDropdown.value = this.value;
                nativeDropdown.selectedIndex = this.options.findIndex(opt => opt.value === this.value);
            } else {
                const newOption = this.allOptions.find(option => option.value === this.value);

                this.selectedText = newOption ? newOption.text : this.placeholder;
            }
        },

        onOptionsClose() {
            this.showOptions = false;
        },
    },
};
</script>

<style lang="scss">
@import '@/styles/_includes.scss';

.dropdown {
    $this: &;
    position: relative;
    color: var(--form-inputColor);
    background-color: var(--form-inputBackgroundColor);
    border: 1px solid var(--form-inputBorderColor);
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 0.8125rem;

    &--native {
        width: 100%;
        padding: 0;
    }

    &--minimalistic {
        border: none;
        padding: 0 32px 0 0;
    }

    &--toolbar {
        color: inherit;
        background-color: inherit;
        border: none;
    }

    &--underline {
        background-color: transparent;
        min-width: inherit !important;
        padding: 0.375rem 2rem 0.375rem 0.75rem;
        // leave the borders split up like this, it's needed for the "invalid" state in flex forms
        // when .is-invalid is added, all of the borders get turned to red
        border-top-color: transparent;
        border-left-color: transparent;
        border-right-color: transparent;
        border-bottom-color: var(--form-inputBorderColor);
    }

    &:hover {
        cursor: pointer;
    }

    &__text {
        display: inline-block;
        vertical-align: middle;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;

        #{ $this }--minimalistic & {
            font-weight: 600;
        }

        #{ $this }--toolbar & {
            font-size: 13px;
        }
    }

    &__arrow {
        position: absolute;
        right: 0;
        margin-right: 0.5rem;
    }

    &__list {
        position: absolute;
        left: -1px;
        right: -1px;
        min-width: min-content;
        overflow-y: auto;
        max-height: 20rem;
        border: 1px solid var(--form-inputBorderColor);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        z-index: 10;
        text-align: left;
        font-weight: 400;

        &.#{ $is-hidden } {
            opacity: 0;
            z-index: -1;
        }

        &--up { top: 100%; }
        &--down { bottom: 100%; }

        #{ $this }--minimalistic & {
            margin: 0.5rem 0 0 -0.25rem;
        }
    }

    &__item {
        color: var(--form-inputColor);
        background-color: var(--form-inputBackgroundColor);
        padding: 0.5rem 1rem;
        white-space: nowrap;

        &:hover {
            cursor: pointer;
            color: var(--form-inputHoverColor);
            background-color: var(--form-inputHoverBackgroundColor);
        }

        &.#{ $is-disabled } {
            color: var(--form-inputColor);
            opacity: 0.65;

            &:hover {
                cursor: default;
                color: var(--form-inputColor);
                background-color: var(--form-inputBackgroundColor);
            }
        }

        #{ $this }--minimalistic & {
            font-weight: 400;
        }
    }

    &__item-special {
        padding: 0.5rem 1rem;
        font-size: 14px;
        text-align: center;
        color: var(--form-buttonColor);
        background-color: var(--form-buttonBackgroundColor);

        &:hover {
            cursor: pointer;
            color: var(--form-buttonHoverColor);
            background-color: var(--form-buttonHoverBackgroundColor);
        }

        #{ $this }--minimalistic & {
            font-weight: 400;
        }
    }
}
</style>
